import { saveAs } from 'file-saver';
import type { Paper, AnalysisResults } from '../types';

// Export to CSV
export function exportToCSV(
  papers: Paper[],
  filename: string,
  includeKeywords: boolean = true
): void {
  const headers = ['PMID', 'Title'];
  if (includeKeywords) {
    headers.push('Matched_Inclusion_Keywords', 'Matched_Exclusion_Keywords', 'Category');
  }

  const rows = papers.map((paper) => {
    const row = [
      paper.pmid,
      `"${paper.title.replace(/"/g, '""')}"`,
    ];

    if (includeKeywords) {
      row.push(
        `"${paper.matchedInclusionKeywords.join(', ')}"`,
        `"${paper.matchedExclusionKeywords.join(', ')}"`,
        paper.category || 'unknown'
      );
    }

    return row.join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `${filename}.csv`);
}

// Export to Excel
export async function exportToExcel(
  results: AnalysisResults,
  filename: string,
  options: {
    includeStatistics: boolean;
    categories: ('inclusion' | 'exclusion' | 'both' | 'none')[];
  }
): Promise<void> {
  const XLSX = await import('xlsx');

  const workbook = XLSX.utils.book_new();

  // Helper to create sheet from papers
  const createSheet = (papers: Paper[], sheetName: string) => {
    const data = papers.map((p) => ({
      PMID: p.pmid,
      Title: p.title,
      Authors: p.authors || '',
      Year: p.year || '',
      Journal: p.journal || '',
      'Inclusion Keywords': p.matchedInclusionKeywords.join(', ') || '(none)',
      'Exclusion Keywords': p.matchedExclusionKeywords.join(', ') || '(none)',
    }));

    const sheet = XLSX.utils.json_to_sheet(data);

    // Set column widths
    sheet['!cols'] = [
      { wch: 12 },  // PMID
      { wch: 80 },  // Title
      { wch: 40 },  // Authors
      { wch: 8 },   // Year
      { wch: 30 },  // Journal
      { wch: 40 },  // Inclusion Keywords
      { wch: 40 },  // Exclusion Keywords
    ];

    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  };

  // Add sheets for selected categories
  if (options.categories.includes('inclusion')) {
    createSheet(results.inclusionOnly, 'Inclusion Only');
  }
  if (options.categories.includes('exclusion')) {
    createSheet(results.exclusionOnly, 'Exclusion Only');
  }
  if (options.categories.includes('both')) {
    createSheet(results.both, 'Both');
  }
  if (options.categories.includes('none')) {
    createSheet(results.none, 'None');
  }

  // Add statistics sheet
  if (options.includeStatistics) {
    const statsData = [
      { Category: 'Total Papers', Count: results.total, Percentage: '100%' },
      { Category: 'Inclusion Only', Count: results.inclusionOnly.length, Percentage: `${((results.inclusionOnly.length / results.total) * 100).toFixed(1)}%` },
      { Category: 'Exclusion Only', Count: results.exclusionOnly.length, Percentage: `${((results.exclusionOnly.length / results.total) * 100).toFixed(1)}%` },
      { Category: 'Both', Count: results.both.length, Percentage: `${((results.both.length / results.total) * 100).toFixed(1)}%` },
      { Category: 'None', Count: results.none.length, Percentage: `${((results.none.length / results.total) * 100).toFixed(1)}%` },
    ];

    const statsSheet = XLSX.utils.json_to_sheet(statsData);
    statsSheet['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(workbook, statsSheet, 'Statistics');
  }

  // Generate and save
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}.xlsx`);
}

// Export to HTML with highlighting
export function exportToHTML(
  papers: Paper[],
  filename: string,
  title: string,
  options: {
    includeHighlighting: boolean;
    inclusionKeywords: string[];
    exclusionKeywords: string[];
  }
): void {
  const highlightTitle = (paperTitle: string): string => {
    if (!options.includeHighlighting) return paperTitle;

    let result = paperTitle;

    // Highlight exclusion keywords (red)
    for (const keyword of options.exclusionKeywords) {
      const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      result = result.replace(
        regex,
        '<mark style="background-color: #fecaca; color: #991b1b; padding: 2px 4px; border-radius: 3px; font-weight: 600;">$1</mark>'
      );
    }

    // Highlight inclusion keywords (green)
    for (const keyword of options.inclusionKeywords) {
      if (options.exclusionKeywords.some((e) => e.toLowerCase() === keyword.toLowerCase())) continue;
      const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      result = result.replace(
        regex,
        '<mark style="background-color: #bbf7d0; color: #166534; padding: 2px 4px; border-radius: 3px; font-weight: 600;">$1</mark>'
      );
    }

    return result;
  };

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title} - Papers Refiner Pro</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #f8fafc;
      color: #1e293b;
      line-height: 1.6;
      padding: 2rem;
    }
    .container { max-width: 1200px; margin: 0 auto; }
    h1 {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: #0f172a;
    }
    .subtitle {
      color: #64748b;
      margin-bottom: 2rem;
    }
    .legend {
      display: flex;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
      padding: 1rem;
      background: white;
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .legend-item { display: flex; align-items: center; gap: 0.5rem; }
    .legend-color {
      width: 20px;
      height: 20px;
      border-radius: 4px;
    }
    .legend-color.inclusion { background: #bbf7d0; }
    .legend-color.exclusion { background: #fecaca; }
    table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    th {
      background: #3b82f6;
      color: white;
      font-weight: 600;
      text-align: left;
      padding: 1rem;
    }
    td {
      padding: 1rem;
      border-bottom: 1px solid #e2e8f0;
      vertical-align: top;
    }
    tr:hover { background: #f8fafc; }
    .pmid {
      font-family: monospace;
      color: #64748b;
      white-space: nowrap;
    }
    .keywords {
      font-size: 0.875rem;
      color: #64748b;
    }
    .keywords span { margin-right: 0.5rem; }
    .keywords .inc { color: #16a34a; }
    .keywords .exc { color: #dc2626; }
    .footer {
      margin-top: 2rem;
      text-align: center;
      color: #94a3b8;
      font-size: 0.875rem;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>${title}</h1>
    <p class="subtitle">Generated by Papers Refiner Pro | ${new Date().toLocaleString()}</p>

    ${options.includeHighlighting ? `
    <div class="legend">
      <div class="legend-item">
        <div class="legend-color inclusion"></div>
        <span>Inclusion Keywords</span>
      </div>
      <div class="legend-item">
        <div class="legend-color exclusion"></div>
        <span>Exclusion Keywords</span>
      </div>
    </div>
    ` : ''}

    <table>
      <thead>
        <tr>
          <th style="width: 100px;">PMID</th>
          <th>Title</th>
          <th style="width: 250px;">Matched Keywords</th>
        </tr>
      </thead>
      <tbody>
        ${papers.map((paper) => `
        <tr>
          <td class="pmid">${paper.pmid}</td>
          <td>${highlightTitle(paper.title)}</td>
          <td class="keywords">
            ${paper.matchedInclusionKeywords.length > 0
              ? `<span class="inc">+ ${paper.matchedInclusionKeywords.join(', ')}</span>`
              : ''}
            ${paper.matchedExclusionKeywords.length > 0
              ? `<span class="exc">- ${paper.matchedExclusionKeywords.join(', ')}</span>`
              : ''}
            ${paper.matchedInclusionKeywords.length === 0 && paper.matchedExclusionKeywords.length === 0
              ? '<span>(none)</span>'
              : ''}
          </td>
        </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="footer">
      <p>Total: ${papers.length} papers | Papers Refiner Pro v1.0</p>
    </div>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  saveAs(blob, `${filename}.html`);
}

// Export all results as ZIP
export async function exportAllAsZip(
  results: AnalysisResults,
  filename: string,
  options: {
    includeHighlighting: boolean;
    inclusionKeywords: string[];
    exclusionKeywords: string[];
  }
): Promise<void> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  // Add CSV files
  const categories = [
    { name: 'inclusion_only', papers: results.inclusionOnly },
    { name: 'exclusion_only', papers: results.exclusionOnly },
    { name: 'both', papers: results.both },
    { name: 'none', papers: results.none },
  ];

  for (const { name, papers } of categories) {
    const headers = ['PMID', 'Title', 'Matched_Inclusion_Keywords', 'Matched_Exclusion_Keywords'];
    const rows = papers.map((paper) => [
      paper.pmid,
      `"${paper.title.replace(/"/g, '""')}"`,
      `"${paper.matchedInclusionKeywords.join(', ')}"`,
      `"${paper.matchedExclusionKeywords.join(', ')}"`,
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    zip.file(`${name}.csv`, '\ufeff' + csv);
  }

  // Add summary log
  const log = generateLogFile(results, options.inclusionKeywords, options.exclusionKeywords);
  zip.file('analysis_log.txt', log);

  // Generate and save
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, `${filename}.zip`);
}

// Generate log file content
function generateLogFile(
  results: AnalysisResults,
  inclusionKeywords: string[],
  exclusionKeywords: string[]
): string {
  const divider = '='.repeat(80);
  const timestamp = new Date().toLocaleString();

  return `${divider}
PAPERS REFINER PRO - ANALYSIS REPORT
Generated: ${timestamp}
${divider}

SUMMARY
${'-'.repeat(40)}
Total Papers Analyzed: ${results.total}
Processing Time: ${results.processingTime.toFixed(0)}ms

RESULTS BY CATEGORY
${'-'.repeat(40)}
Inclusion Only: ${results.inclusionOnly.length} (${((results.inclusionOnly.length / results.total) * 100).toFixed(1)}%)
Exclusion Only: ${results.exclusionOnly.length} (${((results.exclusionOnly.length / results.total) * 100).toFixed(1)}%)
Both:           ${results.both.length} (${((results.both.length / results.total) * 100).toFixed(1)}%)
None:           ${results.none.length} (${((results.none.length / results.total) * 100).toFixed(1)}%)

INCLUSION KEYWORDS (${inclusionKeywords.length})
${'-'.repeat(40)}
${inclusionKeywords.join('\n')}

EXCLUSION KEYWORDS (${exclusionKeywords.length})
${'-'.repeat(40)}
${exclusionKeywords.join('\n')}

${divider}
Papers Refiner Pro v1.0
${divider}
`;
}

// Install jszip if needed
export async function ensureJSZip(): Promise<void> {
  try {
    await import('jszip');
  } catch {
    console.warn('JSZip not available, ZIP export disabled');
  }
}
